import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import useEncodingWS from "./useEncodingWS";
import { useSourceMediaUpload } from "./useSourceMediaUpload";
import EncoderAPI from "../api/EncoderAPI";
import MediaAPI from "../api/MediaAPI";
import {
  deleteSourceMediaMutation,
  deleteStreamMutation,
  startEncodingMutation,
  updateStreamMutation,
} from "../components/encoding/queries/mutations";
import { UPLOAD_STATUS } from "../constants/uploader";
import {
  isFastAssetEncoding,
  isStreamsEncoding,
  isStreamsReadyToPlay,
} from "../utils/encodingStreams";
import { getComputedQuotas } from "../utils/quotas";

import type {
  EncodingProfile,
  EncodingStream,
  GetSourceResponse,
  PivotMedia,
  SourceMedia,
  StartPivotEncodingResponse,
  UpdateStreamData,
  UploadLinkPayload,
} from "../api/EncoderAPI.types";
import type { FastAsset } from "../api/MediaAPI.types";
import type { AppState } from "../reducers";

const FAST_POLLING_AFTER_UPLOAD_MS = 1000;

const SLOW_POOLING_URL_UPLOAD_MS = 10000;

const UPLOAD_QUOTA_THRESHOLD_SECONDS = 3600;

function getSourcesFromResponse(
  response: GetSourceResponse | undefined,
): (SourceMedia | PivotMedia)[] {
  if (!response) return [];

  if (response.type === "media_pivot") {
    return [...response.data, ...response.source_medias];
  }

  return response.data;
}

export type StartEncodingOutcome = "queued" | "allQualitiesAlreadyEncoded";

export interface UseMediaEncodingResult {
  sourceMedias: (SourceMedia | PivotMedia)[];
  streams: EncodingStream[];
  fastAsset: FastAsset | null;
  isEncoding: boolean;
  isReadyToPlay: boolean;
  isLoading: boolean;
  isError: boolean;
  encodingProfiles: EncodingProfile[];
  isProfilesLoading: boolean;
  onDeleteStream: (streamId: string) => void;
  onDeleteSourceMedia: (sourceMediaId: string, filename: string) => void;
  onDeleteFastAsset: () => void;
  onStartEncoding: (profileUuid: string) => Promise<StartEncodingOutcome>;
  isStartingEncoding: boolean;
  onUpdateStream: (streamId: string, data: UpdateStreamData) => void;
  isUploadBlocked: boolean;
  isUploading: boolean;
  isQueued: boolean;
  isUploadError: boolean;
  uploadProgress: number;
  currentFile: File | null;
  uploadedMimeTypeMap: Record<string, string>;
  onAddFile: (files: File[]) => void;
  onResetUpload: () => void;
  onUploadFromUrl: (data: Omit<UploadLinkPayload, "media_uuid">) => void;
}

export function useMediaEncoding(mediaId: string): UseMediaEncodingResult {
  const { t } = useTranslation("editor-notif");
  const queryClient = useQueryClient();

  const hasFastOption = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options?.allow_fast,
  );

  const hasValidMediaId = Boolean(mediaId && mediaId !== "temp_id");

  useEncodingWS({ mediaId });

  const getSourceQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getSource,
    params: [mediaId],
  });

  const getStreamsQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getStreams,
    params: [mediaId],
    options: {
      enabled: hasValidMediaId,
    },
  });

  const getMediaQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMedia,
    params: [mediaId],
  });

  const getFastAssetQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getMediaFastAssets,
    params: [mediaId],
    options: {
      enabled: hasValidMediaId && hasFastOption,
    },
  });

  const getEncodingProfileListQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingProfileList,
    params: [],
  });

  const getEncodingPlanQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingPlan,
    params: [],
  });

  const {
    uploadProgress,
    isUploading,
    isQueued,
    uploadStatus,
    currentFile,
    uploadedFilename,
    uploadedMimeTypeMap,
    onAddFile,
    onResetUpload,
    acknowledgeUpload,
  } = useSourceMediaUpload(mediaId);

  /**
   * Checks if a given source media corresponds to the file that was just uploaded.
   * */
  function isUploadedSource(source: SourceMedia | PivotMedia): boolean {
    if (!uploadedFilename) return false;

    if ("original_filename" in source) {
      return source.original_filename === uploadedFilename;
    }

    return source.filename === uploadedFilename;
  }

  function getSourcePollingInterval(
    response: GetSourceResponse | undefined,
  ): number | false {
    const isUploadCompleted = uploadStatus === UPLOAD_STATUS.COMPLETED;

    const isPivotMediaResponse = response?.type === "media_pivot";

    const isUploadedFileNotYetPresent =
      isUploadCompleted &&
      uploadedFilename !== null &&
      !getSourcesFromResponse(response).some(isUploadedSource);

    if (isUploadedFileNotYetPresent) return FAST_POLLING_AFTER_UPLOAD_MS;

    if (!isPivotMediaResponse) return SLOW_POOLING_URL_UPLOAD_MS;

    return false;
  }

  const { isLoading: isSourceLoading, data: sourceResponse } = useQuery({
    ...getSourceQuery,
    enabled: !isUploading && hasValidMediaId,
    refetchInterval: getSourcePollingInterval,
    onSuccess: (response): void => {
      const isUploadCompleted =
        uploadedFilename && uploadStatus === UPLOAD_STATUS.COMPLETED;
      const isSourceReady =
        response.type === "media_pivot" ||
        response.data.some(
          (source) => isUploadedSource(source) && source.status === "available",
        );

      if (!isUploadCompleted || !isSourceReady) {
        return;
      }

      acknowledgeUpload();

      queryClient.invalidateQueries(getMediaQuery.queryKey);
    },
  });

  const isPivotMediaReady =
    sourceResponse?.type === "media_pivot" &&
    sourceResponse.data.length > 0 &&
    sourceResponse.data.every(
      (source) => source.encoding_status === "transcoded",
    );

  const {
    isLoading: isStreamsLoading,
    isError,
    data: streamsResponse,
  } = useQuery(getStreamsQuery);

  const sourceMedias = useMemo(() => {
    // While encoding (pivots present but not yet transcoded) show only the
    // pivots and hide the raw source medias to avoid duplicating the video.
    // Otherwise — non-pivot responses, pivots ready, or no pivot left (e.g. the
    // last pivot was deleted but VTT files are still attached) — return the
    // full merged set.
    if (
      sourceResponse?.type === "media_pivot" &&
      !isPivotMediaReady &&
      sourceResponse.data.length > 0
    ) {
      return sourceResponse.data;
    }
    return getSourcesFromResponse(sourceResponse);
  }, [sourceResponse, isPivotMediaReady]);

  const streams = useMemo(() => streamsResponse?.data ?? [], [streamsResponse]);

  const isUploadingEffective =
    isUploading ||
    (uploadStatus === UPLOAD_STATUS.COMPLETED &&
      uploadedFilename !== null &&
      !sourceMedias.some(isUploadedSource));

  const isUploadError = uploadStatus === UPLOAD_STATUS.ERROR;

  const { data: encodingPlan } = useQuery(getEncodingPlanQuery);

  // Use costFactor=1 and no source streams to get the raw remaining quota
  // (how much encoding time is left in the pool, independent of any profile).
  const rawQuota = useMemo(
    () => getComputedQuotas([], encodingPlan, 1),
    [encodingPlan],
  );

  const isUploadBlocked =
    rawQuota.available.seconds < UPLOAD_QUOTA_THRESHOLD_SECONDS;

  const { data: fastAssetsResponse } = useQuery(getFastAssetQuery);

  const fastAsset = fastAssetsResponse?.fast_assets ?? null;

  const isReadyToPlay = useMemo(
    () => isPivotMediaReady || isStreamsReadyToPlay(streams, fastAsset),
    [streams, isPivotMediaReady, fastAsset],
  );

  const isEncoding = useMemo(
    () => isStreamsEncoding(streams) || isFastAssetEncoding(fastAsset),
    [streams, fastAsset],
  );

  const { isLoading: isProfilesLoading, data: profilesResponse } = useQuery(
    getEncodingProfileListQuery,
  );

  const encodingProfiles = useMemo(
    () => profilesResponse?.data ?? [],
    [profilesResponse],
  );

  const deleteStreamMutate = useMutation(
    deleteStreamMutation({ mediaId, queryClient }),
  );

  function onDeleteStream(streamId: string): void {
    deleteStreamMutate.mutate({ streamId });
  }

  const updateStreamMutate = useMutation(
    updateStreamMutation({ mediaId, queryClient }),
  );

  function onUpdateStream(streamId: string, data: UpdateStreamData): void {
    updateStreamMutate.mutate({ streamId, ...data });
  }

  const deleteSourceMediaMutate = useMutation(
    deleteSourceMediaMutation({ mediaId, queryClient }),
  );

  function onDeleteSourceMedia(sourceMediaId: string, filename: string): void {
    // Clear lingering upload state when removing the just-uploaded file or the
    // last remaining source — but never while a transfer is in progress, which
    // would abort it (e.g. deleting a VTT during an unrelated upload).
    if (
      !isUploading &&
      (sourceMedias.length === 1 || filename === uploadedFilename)
    )
      onResetUpload();
    deleteSourceMediaMutate.mutate({ sourceMediaId, filename });
  }

  const deleteFastAssetMutate = useMutation({
    mutationFn: async (uuid: string) => EncoderAPI.deleteFastAsset(uuid),
    onSuccess: (): void => {
      toast.success(t("deleteStreamFastSuccess"));
      queryClient.invalidateQueries(getFastAssetQuery.queryKey);
    },
    onError: (): void => {
      toast.error(t("deleteStreamFastError"));
    },
  });

  function onDeleteFastAsset(): void {
    if (!fastAsset) return;
    deleteFastAssetMutate.mutate(fastAsset.uuid);
  }

  const startEncodingMutateConfig = startEncodingMutation({
    mediaId,
    queryClient,
  });

  const startEncodingMutate = useMutation<
    StartPivotEncodingResponse,
    unknown,
    { profileUuid: string }
  >({
    ...startEncodingMutateConfig,
    onSuccess: (data, variables, context): void => {
      startEncodingMutateConfig.onSuccess?.(data, variables, context);
      if (data?.results?.[0]?.status === "all_qualities_already_encoded") {
        toast.warning(t("allQualitiesEncoded"), {
          duration: 4000,
        });
      }
    },
  });

  const isStartingEncoding = startEncodingMutate.isLoading;

  async function onStartEncoding(
    profileUuid: string,
  ): Promise<StartEncodingOutcome> {
    const response = await startEncodingMutate.mutateAsync({ profileUuid });

    return response?.results?.[0]?.status === "all_qualities_already_encoded"
      ? "allQualitiesAlreadyEncoded"
      : "queued";
  }

  const uploadFromUrlMutate = useMutation({
    mutationFn: async (data: UploadLinkPayload) => EncoderAPI.uploadLink(data),
    onSuccess: (): void => {
      queryClient.invalidateQueries(getSourceQuery.queryKey);
    },
    onError: (): void => {
      toast.error(t("uploadFailed"));
    },
  });

  function onUploadFromUrl(data: Omit<UploadLinkPayload, "media_uuid">): void {
    uploadFromUrlMutate.mutate({ ...data, media_uuid: mediaId });
  }

  return {
    sourceMedias,
    streams,
    fastAsset,
    isEncoding,
    isReadyToPlay,
    isLoading: isSourceLoading || isStreamsLoading,
    isError,
    encodingProfiles,
    isProfilesLoading,
    onDeleteStream,
    onDeleteSourceMedia,
    onDeleteFastAsset,
    onStartEncoding,
    isStartingEncoding,
    onUpdateStream,
    isUploadBlocked,
    isUploading: isUploadingEffective,
    isQueued,
    isUploadError,
    uploadProgress,
    currentFile,
    uploadedMimeTypeMap,
    onAddFile,
    onResetUpload,
    onUploadFromUrl,
  };
}
